import { useCallback, useState, useMemo } from 'react';
import type { ActionMeta, MultiValue, FormatOptionLabelMeta, MultiValueGenericProps, StylesConfig, ValueContainerProps, GroupBase, MultiValueProps } from 'react-select';
import Select, { components } from 'react-select';
import styled from '@emotion/styled';
import { useQuery } from '@tanstack/react-query';
import type { BrandInsightsInstanceContext, TagFilter } from '@trinity/chat';
import { Chip } from '@trinity/chat-components/components/presentation';
import { useTheme } from '@trinity/chat-components/components/Theme';
import { useAppTheming } from '@trinity/chat-components/containers/AppTheming/index.js';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
import * as Presentation from './Presentation';

type Props = {
  context: BrandInsightsInstanceContext.ItemWithMetadata;
  value: TagFilter.Item[];
  isSidePanel?: boolean;
  onChange: (value: TagFilter.Item[], meta: ActionMeta<TagFilter.Item>) => unknown;
};


function withSidePanelMenuStyles(
  base: StylesConfig<TagFilter.Item, true>
): StylesConfig<TagFilter.Item, true> {
  return {
    ...base,
    menu: (provided, props) => ({
      ...(base.menu ? base.menu(provided, props) : provided),
      width: '100%',
      minWidth: 0,
      maxWidth: '100%',
      boxSizing: 'border-box',
    }),
    menuPortal: (provided, props) => ({
      ...(base.menuPortal ? base.menuPortal(provided, props) : provided),
      boxSizing: 'border-box',
    }),

    option: (provided, props) => ({
      ...(base.option ? base.option(provided, props) : provided),
      boxSizing: 'border-box',
      maxWidth: '100%',
    }),
    control: (provided, props) => ({
      ...(base.control ? base.control(provided, props) : provided),
      boxSizing: 'border-box',
      maxWidth: '100%',
    }),
    valueContainer: (provided, props) => ({
      ...(base.valueContainer ? base.valueContainer(provided, props) : provided),
      boxSizing: 'border-box',
      minWidth: 0,
      maxWidth: '100%',
      overflowX: 'hidden',
    }),
  };
}

export function Tag({ context, value, isSidePanel, onChange }: Props) {
  const theme = useTheme();
  const theming = useAppTheming();

  const selectStyles = useMemo(() => {
    const base = Presentation.buildSelectStyles<TagFilter.Item, true>(theme);
    return isSidePanel ? withSidePanelMenuStyles(base) : base;
  }, [theme, isSidePanel]);
  const selectTheme = useMemo(() => Presentation.buildSelectTheme(theme), [theme]);

  const selected = useMemo(() => value, [value]);

  const handleChange = useCallback(
    (value: MultiValue<TagFilter.Item>, meta: ActionMeta<TagFilter.Item>) => {
      if (meta.action === 'clear') {
        const immutableTags = selected.filter((t) => t.metadata?.canRemove === false);
        onChange(immutableTags, meta);
      } else {
        onChange(Array.from(value), meta);
      }
    },
    [onChange, selected]
  );

  const [inputValue, setInputValue] = useState('');

  const apiClient = useChatApiClient();

  const optionsQuery = useQuery({
    queryKey: [`get:brand-insights/chat/filters/tags`, context, inputValue],
    queryFn: async () => {
      return apiClient.chat
        .searchTags({
          context,
          query: inputValue,
        })
        .then((r) => r.data.results);
    },
    placeholderData: [],
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    staleTime: 5000,
  });

  const handleInputChange = useCallback((newValue: string) => setInputValue(newValue), []);

  const handleNoOptionsMessage = useCallback(
    (obj: { inputValue: string }) => {
      if (optionsQuery.isFetching) return `Loading...`;
      return obj.inputValue?.length ? `No tags found` : null;
    },
    [optionsQuery.isFetching]
  );

  const formatOptionLabel = useCallback(
    (option: TagFilter.Item, metadata: FormatOptionLabelMeta<TagFilter.Item>) => {
      const isInDropdown = metadata.context === 'menu';

      // White label theming for Chip via CSS custom properties
      const chipStyles: React.CSSProperties | undefined =
        theming.enabled && theming.palette.primary
          ? ({
              '--chip-bg': isInDropdown ? 'transparent' : theming.palette.primary.lighter,
              '--chip-border': theming.palette.primary.main,
              '--chip-color': theming.palette.primary.main,
            } as React.CSSProperties)
          : undefined;

      return (
        <div
          style={
            isSidePanel
              ? { display: 'flex', alignItems: 'center', gap: '6px', width: '100%', minWidth: 0, pointerEvents: 'none' }
              : { display: 'flex', alignItems: 'center', gap: '6px', pointerEvents: 'none' }
          }
        >
          {isSidePanel ? (
            <OptionLabelText title={option.name}>{option.name}</OptionLabelText>
          ) : (
            <span>{option.name}</span>
          )}
          {!!option.metadata?.segmentIdentifier && (
            <StyledChip selected={true} style={chipStyles}>
              Segment
            </StyledChip>
          )}
          {typeof option.metadata?.journeyId === 'number' && (
            <StyledChip selected={true} style={chipStyles}>
              Journey
            </StyledChip>
          )}
        </div>
      );
    },
    [theming, isSidePanel]
  );

  const ValueContainerComponent = useCallback(
    (props: ValueContainerProps<TagFilter.Item, true, GroupBase<TagFilter.Item>>) => (
      <CollapsedTagValueContainer {...props} isSidePanel={isSidePanel} />
    ),
    [isSidePanel]
  );

  const MultiValueComponent = useCallback(
    (props: MultiValueProps<TagFilter.Item, true>) => <TagPill {...props} isSidePanel={isSidePanel} />,
    [isSidePanel]
  );

  return (
    <Presentation.Row>
      <Presentation.Label>Tags</Presentation.Label>
      <TagValue isSidePanel={isSidePanel}>
        <Select<TagFilter.Item, true>
          styles={selectStyles}
          components={{
            DropdownIndicator: null,
            MultiValue: MultiValueComponent,
            MultiValueRemove: MultiValueRemove,
            ValueContainer: ValueContainerComponent,
          }}
          theme={selectTheme}
          value={selected}
          isMulti
          isSearchable
          isClearable
          isLoading={optionsQuery.isFetching}
          options={optionsQuery.data}
          placeholder={`Search tags...`}
          noOptionsMessage={handleNoOptionsMessage}
          formatOptionLabel={formatOptionLabel}
          getOptionLabel={(o) => o.name}
          getOptionValue={(o) => `${o.id}`}
          onChange={handleChange}
          onInputChange={handleInputChange}
          menuPlacement={isSidePanel ? 'top' : 'auto'}
          menuPortalTarget={isSidePanel ? document.body : undefined}
          menuPosition={isSidePanel ? 'fixed' : undefined}
        />
      </TagValue>
    </Presentation.Row>
  );
}

const TagValue = styled(Presentation.Value)<{ isSidePanel?: boolean }>(({ isSidePanel }) => ({
  minWidth: 0,
  overflow: isSidePanel ? 'hidden' : undefined,
}));

type ValueContainerProps_Extended = ValueContainerProps<TagFilter.Item, true, GroupBase<TagFilter.Item>> & {
  isSidePanel?: boolean;
};

function CollapsedTagValueContainer(
  props: ValueContainerProps_Extended,
) {
  const { children, getValue, selectProps, isSidePanel } = props;
  const values = getValue();

  const inputChild = Array.isArray(children) ? children[children.length - 1] : null;

  const isSearching = Boolean((selectProps as { inputValue?: string }).inputValue);

  if (isSidePanel && values.length > 0 && !isSearching) {
    return (
      <components.ValueContainer {...props}>
        <TagPillContainer>
          {values.map((tag) => (
            <TagPillWrapper key={tag.id} data={tag} />
          ))}
        </TagPillContainer>
        {inputChild}
      </components.ValueContainer>
    );
  }

  if (values.length === 0 || isSearching) {
    return (
      <components.ValueContainer {...props}>
        {children}
      </components.ValueContainer>
    );
  }

  const [first, ...rest] = values;
  const label =
    rest.length > 0
      ? `${first.name} +${rest.length} other${rest.length > 1 ? 's' : ''}`
      : first.name;

  return (
    <components.ValueContainer {...props}>
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {label}
      </span>
      {inputChild}
    </components.ValueContainer>
  );
}

const MultiValueRemove = (props: MultiValueGenericProps<TagFilter.Item, true>) => {
  if (props.data.metadata?.canRemove === false) {
    return null;
  }
  return <components.MultiValueRemove {...props} />;
};

// ─── Pill Container ────────────────────────────────────────────────────────
const TagPillContainer = styled.div({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '6px',
  marginBottom: '6px',
  width: '100%',
  minWidth: 0,
});

// ─── Individual Tag Pill (used in ValueContainer) ──────────────────────────
function TagPillWrapper({ data }: { data: TagFilter.Item }) {
  const theming = useAppTheming();

  const chipStyles: React.CSSProperties | undefined =
    theming.enabled && theming.palette.primary
      ? ({
          '--chip-bg': theming.palette.primary.lighter,
          '--chip-border': theming.palette.primary.main,
          '--chip-color': theming.palette.primary.main,
        } as React.CSSProperties)
      : undefined;

  return (
    <TagPillItem style={chipStyles}>
      <TagPillLabel>{data.name}</TagPillLabel>
      {!!data.metadata?.segmentIdentifier && (
        <PillMetadataBadge>Segment</PillMetadataBadge>
      )}
      {typeof data.metadata?.journeyId === 'number' && (
        <PillMetadataBadge>Journey</PillMetadataBadge>
      )}
    </TagPillItem>
  );
}

// ─── Multi Value Pill Component (react-select integration) ─────────────────
type TagPillProps = MultiValueProps<TagFilter.Item, true> & {
  isSidePanel?: boolean;
};

function TagPill(props: TagPillProps) {
  const theming = useAppTheming();
  const { isSidePanel } = props;

  if (!isSidePanel) {
    return <components.MultiValue {...props} />;
  }

  const chipStyles: React.CSSProperties | undefined =
    theming.enabled && theming.palette.primary
      ? ({
          '--chip-bg': theming.palette.primary.lighter,
          '--chip-border': theming.palette.primary.main,
          '--chip-color': theming.palette.primary.main,
        } as React.CSSProperties)
      : undefined;

  const canRemove = props.data.metadata?.canRemove !== false;

  return (
    <TagPillItem style={chipStyles}>
      <TagPillLabel>{props.data.name}</TagPillLabel>
      {!!props.data.metadata?.segmentIdentifier && (
        <PillMetadataBadge>Segment</PillMetadataBadge>
      )}
      {typeof props.data.metadata?.journeyId === 'number' && (
        <PillMetadataBadge>Journey</PillMetadataBadge>
      )}
      {canRemove && (
        <PillRemoveButton
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            // @ts-expect-error react-select type incompatibility between div and button click handlers
            props.removeProps.onClick?.(e);
          }}
          aria-label={`Remove tag ${props.data.name}`}
        >
          ×
        </PillRemoveButton>
      )}
    </TagPillItem>
  );
}

const OptionLabelText = styled.span({
  flex: '0 1 auto',
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

const StyledChip = styled(Chip)({
  borderRadius: 12,
  padding: '2px 5px',
  flexShrink: 0,
});

// ─── Tag Pill Styles ──────────────────────────────────────────────────────
// Font size/family match Presentation.buildSelectStyles (used by Published Date,
// Project, and Record Type filters) so all Side Panel filter values render consistently.
const TagPillItem = styled.div(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  padding: '6px 10px',
  borderRadius: '16px',
  backgroundColor: 'var(--chip-bg, #f0f0f0)',
  border: '1px solid var(--chip-border, #ccc)',
  color: 'var(--chip-color, #333)',
  fontSize: '14px',
  fontFamily: theme.fonts.regular,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  minWidth: 0,
  maxWidth: '100%',
}));

const TagPillLabel = styled.span({
  flex: '0 1 auto',
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  pointerEvents: 'none',
});

const PillMetadataBadge = styled.span({
  fontSize: '11px',
  padding: '2px 5px',
  borderRadius: '8px',
  backgroundColor: 'var(--chip-border, #ccc)',
  color: 'white',
  whiteSpace: 'nowrap',
  flexShrink: 0,
  pointerEvents: 'none' as const,
});

const PillRemoveButton = styled.button({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '18px',
  height: '18px',
  border: 'none',
  backgroundColor: 'transparent',
  color: 'inherit',
  cursor: 'pointer',
  fontSize: '16px',
  fontWeight: 'bold',
  lineHeight: 1,
  padding: '0 2px',
  margin: 0,

  '&:hover': {
    opacity: 0.7,
  },
});
